import React from 'react';
import { Card, Col, Row, Skeleton, Typography, theme } from 'antd';
import { AndroidFilled, AppleFilled } from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { useTheme } from '../../../contexts/ui/ThemeContext';
import { EThemeNames } from '../../../../domain/types/themePresets';
import { platformLabel } from './callCells';
import { AI_RECEPTIONIST_VIOLET, CATEGORICAL_COLORS } from './chartColors';
import type { CallsAggregate } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface BreakdownPanelProps {
  breakdown: CallsAggregate['breakdown'];
  /** Utilisateurs mobiles par plateforme — hors période, voir `CENTER_LABELS`. */
  devices: Record<string, number>;
  status: ContextStatus;
}

type BreakdownDimension = 'direction' | 'platform' | 'device';

const DIRECTION_LABELS: Record<string, string> = {
  inbound: 'Entrant',
  outbound: 'Sortant',
};

// `Platform.OS` tel qu'écrit par l'app mobile.
const DEVICE_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

// Marques officielles, prises dans `@ant-design/icons` déjà embarqué — aucun
// asset à télécharger, aucun logo tiers copié dans le dépôt.
const DEVICE_ICONS: Record<string, React.ReactNode> = {
  ios: <AppleFilled />,
  android: <AndroidFilled />,
};

/** Vert officiel de la marque Android. */
const ANDROID_GREEN = '#3DDC84';

// Les libellés de plateforme viennent de `callCells` : ce panneau et les deux
// tables d'appels doivent nommer `livekit` de la même façon.
const labelFor = (dimension: BreakdownDimension, key: string): string => {
  if (dimension === 'direction') return DIRECTION_LABELS[key] ?? key;
  if (dimension === 'device') return DEVICE_LABELS[key] ?? key;
  return platformLabel(key);
};

// `breakdown.direction`/`.platform` n'ajoutent une clé qu'à partir d'un appel
// dont la valeur est connue (`bump`, backend/src/helpers/leedflow/overview-model.ts
// ignore les clés nulles) : un appel à direction inconnue, par exemple,
// n'apparaît dans AUCUNE des entrées de `breakdown.direction`. Le
// donut ci-dessous calcule donc ses pourcentages sur la somme des parts
// listées ici, pas sur `handledCalls` — jamais présenté comme une part de
// « tous les appels traités », seulement comme une part parmi les appels dont
// cette dimension est connue.
const buildRows = (record: Record<string, number>, dimension: BreakdownDimension) =>
  Object.entries(record)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({
      key,
      label: labelFor(dimension, key),
      value,
    }));

const EMPTY_LABELS: Record<BreakdownDimension, string> = {
  direction: 'Aucune donnée sur la période',
  platform: 'Aucune donnée sur la période',
  device: 'Aucun appareil connu',
};

const BreakdownPanel: React.FC<BreakdownPanelProps> = ({ breakdown, devices, status }) => {
  const { token } = useToken();
  const { themeName } = useTheme();
  const isDark = themeName === EThemeNames.LEEDFLOW_DARK;

  // L'onglet Appareil sort de la palette catégorielle : ces deux plateformes
  // ont des couleurs de marque que tout le monde reconnaît. Le noir d'Apple
  // devient blanc cassé en thème sombre, comme le fait Apple lui-même — un noir
  // pur sur fond sombre serait une part invisible.
  const deviceColors: Record<string, string> = {
    ios: isDark ? '#F5F5F7' : '#000000',
    android: ANDROID_GREEN,
  };

  // Le Réceptionniste IA sort de la palette : les deux verts disent « une ligne
  // téléphonique », lui n'en est pas une. VoIP et GSM sont épinglés eux aussi,
  // sinon leurs couleurs s'échangeraient le jour où les volumes s'inversent —
  // une plateforme ne doit pas changer de couleur d'une période à l'autre.
  const platformColors: Record<string, string> = {
    livekit: CATEGORICAL_COLORS[0],
    callr: CATEGORICAL_COLORS[1],
    elevenlabs_receptionist: AI_RECEPTIONIST_VIOLET,
  };

  const fixedColors: Partial<Record<BreakdownDimension, Record<string, string>>> = {
    device: deviceColors,
    platform: platformColors,
  };

  const colorsFor = (rows: ReturnType<typeof buildRows>, dimension: BreakdownDimension): string[] => {
    const fixed = fixedColors[dimension];
    if (!fixed) return CATEGORICAL_COLORS;
    return rows.map((row, i) => fixed[row.key] ?? CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length]);
  };

  const donutOptions = (
    rows: ReturnType<typeof buildRows>,
    dimension: BreakdownDimension,
  ): ApexOptions => {
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    const pctOf = (value: number) => (total > 0 ? (value / total) * 100 : 0);

    return {
      chart: { type: 'donut', background: 'transparent' },
      labels: rows.map((row) => row.label),
      colors: colorsFor(rows, dimension),
      stroke: { width: 0 },
      // Légende maison sous le graphe : celle d'ApexCharts ne rend que du
      // texte, or l'onglet Appareil porte les icônes Apple et Android.
      legend: { show: false },
      dataLabels: {
        formatter: (pct: number) => `${pct.toFixed(1)}%`,
        style: { fontSize: '12px' },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '62%',
            labels: {
              show: true,
              // Au repos comme au survol, le centre affiche un POURCENTAGE :
              // au repos celui de la part dominante (`rows[0]`, la liste est
              // triée décroissante), au survol celui de la part pointée. Le
              // nom au-dessus dit toujours de qui il s'agit. Les effectifs
              // bruts, eux, restent dans la légende.
              name: { color: token.colorTextSecondary, fontSize: '13px' },
              value: {
                color: token.colorText,
                fontSize: '22px',
                fontWeight: 600,
                formatter: (value: string) => `${pctOf(Number(value)).toFixed(1)}%`,
              },
              total: {
                show: true,
                label: rows[0]?.label ?? '',
                color: token.colorTextSecondary,
                fontSize: '13px',
                formatter: () => `${pctOf(rows[0]?.value ?? 0).toFixed(1)}%`,
              },
            },
          },
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: { formatter: (value: number) => formatNumber(value) },
      },
    };
  };

  const records: Record<BreakdownDimension, Record<string, number>> = {
    direction: breakdown.direction,
    platform: breakdown.platform,
    device: devices,
  };

  // Une colonne par dimension plutôt que des onglets : les trois répartitions
  // se lisent d'un coup, sans clic ni mémoire de ce que disait l'autre onglet.
  const buildColumn = (dimension: BreakdownDimension, label: string) => {
    const rows = buildRows(records[dimension], dimension);

    return (
      <Col xs={24} md={8} key={dimension}>
        <Typography.Text strong style={{ display: 'block', textAlign: 'center' }}>
          {label}
        </Typography.Text>

        {rows.length === 0 ? (
          <div style={{ color: token.colorTextSecondary, textAlign: 'center', padding: '4rem 0' }}>
            {EMPTY_LABELS[dimension]}
          </div>
        ) : dimension === 'device' ? (
          // Deux parts seulement, et la question posée est « laquelle est la
          // plus grosse, et de combien ». Deux barres alignées sur la même
          // origine répondent à l'oeil ; un angle demande une conversion
          // mentale. Fait en CSS plutôt qu'en ApexCharts : la lib place ses
          // étiquettes dans la barre, où le noir d'Apple les avale.
          <div
            style={{
              height: 240,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 32,
              padding: '0 4px',
            }}
          >
            {rows.map((row, i) => {
              const total = rows.reduce((sum, r) => sum + r.value, 0);
              const pct = total > 0 ? (row.value / total) * 100 : 0;
              const color = colorsFor(rows, dimension)[i];

              return (
                <div key={row.key}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-flex', color, fontSize: 16 }}>
                        {DEVICE_ICONS[row.key] ?? null}
                      </span>
                      <span style={{ color: token.colorText, fontWeight: 500 }}>{row.label}</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                      <span
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: token.colorText,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {pct.toFixed(1)}%
                      </span>
                      <span
                        style={{
                          color: token.colorTextSecondary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatNumber(row.value)}
                      </span>
                    </span>
                  </div>

                  <div
                    style={{
                      height: 12,
                      borderRadius: 999,
                      background: token.colorFillSecondary,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 999,
                        background: color,
                        transition: 'width 450ms ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <ReactApexChart
              options={donutOptions(rows, dimension)}
              series={rows.map((row) => row.value)}
              type="donut"
              height={240}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 16,
                color: token.colorTextSecondary,
              }}
            >
              {rows.map((row, i) => (
                <span key={row.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: colorsFor(rows, dimension)[i],
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    {row.label} — <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(row.value)}</span>
                  </span>
                </span>
              ))}
            </div>
          </>
        )}
      </Col>
    );
  };

  return (
    <Card>
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Row gutter={[16, 24]}>
          {buildColumn('direction', 'Entrant vs Sortant')}
          {buildColumn('platform', 'GSM vs VoIP vs Réceptionniste')}
          {buildColumn('device', 'iOS vs Android')}
        </Row>
      )}
    </Card>
  );
};

export default BreakdownPanel;
